// Chakra imports
import { Box, ChakraProvider, Portal, useDisclosure } from '@chakra-ui/react';
import { RtlProvider } from '../components/RTLProvider/RTLProvider';
import Configurator from '../components/Configurator/Configurator';
import Footer from '../components/Footer/Footer';
// Layout components
import AdminNavbar from '../components/Navbars/AdminNavbar';
import Sidebar from '../components/Sidebar';
import React, { useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import routes from '../routes';
// Custom Chakra theme
import theme from '../theme/theme';
// Custom components
import MainPanel from '../components/Layout/MainPanel';
import PanelContainer from '../components/Layout/PanelContainer';
import AppPanelRouteHost from '../components/Layout/AppPanelRouteHost';
import '@fontsource/urbanist/300.css';
import '@fontsource/urbanist/400.css';
import '@fontsource/urbanist/500.css';
import '@fontsource/urbanist/600.css';
import '@fontsource/urbanist/700.css';
import PanelContent from '../components/Layout/PanelContent';
import { RouteConfigArray } from '../types/routes';
import { SIDEBAR_WIDTH_TRANSITION } from '../constants/transitions';
import { DisplayTimezoneProvider } from '../contexts/DisplayTimezoneContext';
import { DisplayCurrencyProvider } from '../contexts/DisplayCurrencyContext';

interface DashboardProps {
  [key: string]: any;
}

export default function Dashboard(props: DashboardProps) {
	const { ...rest } = props;
	// states and functions
	const [ sidebarVariant, setSidebarVariant ] = useState<string>('opaque');
	const [ fixed, setFixed ] = useState<boolean>(false);
	
	const getRoute = (): boolean => {
		return window.location.pathname !== '/full-screen-maps';
	};
	
	const getActiveRoute = (routes: RouteConfigArray): string => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return 'Default Brand Text';
		}
		let activeRoute = 'Default Brand Text';
		for (let i = 0; i < routes.length; i++) {
			const route = routes[i];
			if (route.collapse && route.views) {
				const collapseActiveRoute = getActiveRoute(route.views);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else if (route.category && route.views) {
				const categoryActiveRoute = getActiveRoute(route.views);
				if (categoryActiveRoute !== activeRoute) {
					return categoryActiveRoute;
				}
			} else {
				if (route.layout && route.path && window.location.href.indexOf(route.layout + route.path) !== -1) {
					return route.name;
				}
			}
		}
		return activeRoute;
	};
	
	// This changes navbar state(fixed or not)
	const getActiveNavbar = (routes: RouteConfigArray): boolean => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return false;
		}
		let activeNavbar = false;
		for (let i = 0; i < routes.length; i++) {
			const route = routes[i];
			if (route.category && route.views) {
				const categoryActiveNavbar = getActiveNavbar(route.views);
				if (categoryActiveNavbar !== activeNavbar) {
					return categoryActiveNavbar;
				}
			} else {
				if (route.layout && route.path && window.location.href.indexOf(route.layout + route.path) !== -1) {
					if (route.secondaryNavbar) {
						return route.secondaryNavbar;
					}
				}
			}
		}
		return activeNavbar;
	};
	
	const getRoutes = (routes: RouteConfigArray): JSX.Element[] => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return [];
		}
		const routeList: JSX.Element[] = [];
		routes.forEach((prop, key) => {
			if (prop.collapse && prop.views) {
				routeList.push(...getRoutes(prop.views));
			} else if (prop.category === 'account' && prop.views) {
				routeList.push(...getRoutes(prop.views));
			} else if ((prop.layout === '/rtl' || prop.layout === '/admin') && prop.path && prop.component) {
				routeList.push(<Route path={prop.layout + prop.path} component={prop.component} key={key} />);
			}
		});
		return routeList;
	};
	
	const { isOpen, onOpen, onClose } = useDisclosure();
	document.documentElement.dir = 'rtl';
	
	// Sidebar collapse state
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
		const saved = localStorage.getItem('sidebarCollapsed');
		return saved ? JSON.parse(saved) : false;
	});

	const toggleSidebar = () => {
		const newState = !isSidebarCollapsed;
		setIsSidebarCollapsed(newState);
		localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
	};
	
	// Chakra Color Mode
	return (
		<ChakraProvider theme={theme as any} resetCss={false} {...({} as any)}>
			<Box data-app-route-surface minH="100vh" w="100%">
			<DisplayTimezoneProvider>
			<DisplayCurrencyProvider>
			<RtlProvider>
				<Sidebar
					routes={routes as RouteConfigArray}
					logoText={'PURITY UI DASHBOARD'}
					display='none'
					sidebarVariant={sidebarVariant}
					isCollapsed={isSidebarCollapsed}
					onToggleSidebar={toggleSidebar}
					{...rest}
				/>
				<MainPanel
					variant='rtl'
					w={{
						base: '100%',
						xl: isSidebarCollapsed ? 'calc(100% - 95px)' : 'calc(100% - 275px)'
					}}
					transition={SIDEBAR_WIDTH_TRANSITION}
					style={{
						willChange: 'width',
						backfaceVisibility: 'hidden',
						transform: 'translateZ(0)',
					}}>
					<Portal>
						<AdminNavbar
							onOpen={onOpen}
							logoText={'PURITY UI DASHBOARD'}
							brandText={getActiveRoute(routes as RouteConfigArray)}
							secondary={getActiveNavbar(routes as RouteConfigArray)}
							fixed={fixed}
							display={{ base: 'flex', xl: 'none' }}
							{...rest}
						/>
					</Portal>
					{getRoute() ? (
						<PanelContent>
							<PanelContainer>
								<AppPanelRouteHost>
									<Switch>
										{getRoutes(routes as RouteConfigArray)}
										<Redirect from='/rtl' to='/rtl/rtl-support-page' />
									</Switch>
								</AppPanelRouteHost>
							</PanelContainer>
						</PanelContent>
					) : null}
					<Footer />
					<Configurator
						secondary={getActiveNavbar(routes as RouteConfigArray)}
						isOpen={isOpen}
						onClose={onClose}
						isChecked={fixed}
						onSwitch={(value: boolean) => {
							setFixed(value);
						}}
						onOpaque={() => setSidebarVariant('opaque')}
						onTransparent={() => setSidebarVariant('transparent')}
						{...({} as any)}
					/>
				</MainPanel>
			</RtlProvider>
			</DisplayCurrencyProvider>
			</DisplayTimezoneProvider>
			</Box>
		</ChakraProvider>
	);
}

